import {
  convertDashedUSPhoneNumberToInternationalFormat,
  coalescePlainTextToEditorV2Html,
  DateFormat,
  FAIconName,
  isPossiblePhoneNumber,
  TagColor,
} from '@equip.health/ui';
import { isEmpty } from 'lodash';
import { DateTime } from 'luxon';

import type { PatientGroupClassListItem } from '~/components/maud/patients/profile/tabs/appointments/groupClasses';

import { getExtension } from './chat/chat.utils';
import util from './util';
import { InquiryStatusValue } from '../constants/inquiry.constants';
import {
  type CustomShippingAddress,
  CommonLanguage,
  CommonTreatmentProgram,
  ConsentToRecord,
  disallowedUniversalStatusesForOrderingLab,
  EMPTY_DATE_FORMAT,
  EMPTY_STRING,
  EpisodeStatus,
  LabOrderEligibleProviderType,
  MULTIPLE_PRONOUNS_DELIMITER,
  openUniversalStatuses,
  PatientUniversalStatusCode,
  PREFER_NOT_TO_ANSWER_LABEL,
  TABLE_DEFAULT_COLUMNS,
  TreatmentEpisodeStatus,
} from '../constants/patients.constants';

import type { EditBasicInfoFormState } from '~/lib/types/patient/EditBasicInfoFormState';

export const getAgeFromDOB = (
  dob: string,
  fromFormat = 'LL/dd/yyyy',
): number => {
  if (!dob || !DateTime.fromFormat(dob, fromFormat).isValid) return null;
  return Math.floor(
    Math.abs(DateTime.fromFormat(dob, fromFormat).diffNow('years').as('years')),
  );
};

export const getExactAgeFromDOB = (
  dob: string,
  fromFormat = 'LL/dd/yyyy',
  ageFormat: 'short' | 'long' = 'short',
): string => {
  if (!dob || !DateTime.fromFormat(dob, fromFormat).isValid) return null;
  const years = Math.floor(
    Math.abs(DateTime.fromFormat(dob, fromFormat).diffNow('years').as('years')),
  );
  const months = Math.floor(
    Math.abs(
      DateTime.fromFormat(dob, fromFormat).diffNow('months').as('months'),
    ) % 12,
  );

  if (ageFormat === 'long') {
    return `${years} yr${months > 0 ? `, ${months} mo` : ''}`;
  }

  return `${years} y${months > 0 ? `, ${months} m` : ''}.`;
};

export const getName = (name: string): string => (name ?? '').trim();

export const getPreferredFirstName = (
  chosenName: string,
  firstName: string,
  useQuotes = true,
): string => {
  const chosen = getName(chosenName);
  const first = getName(firstName);

  /* Explicitly check for false for backwards compatibility
    That way both undefined and true will default to use quotes.
  */
  if (useQuotes === false) {
    return chosen.length ? chosen : first;
  }

  return chosen.length ? `${first} "${chosen}"` : first;
};

export const getPronounsValue = (pronouns: string[]): string => {
  return pronouns?.length > 0
    ? pronouns.join(MULTIPLE_PRONOUNS_DELIMITER)
    : EMPTY_STRING;
};

export const formGenderString = (
  gender: string,
  pronoun: string,
  recordString: string,
): string => {
  let genderText = '';

  if (gender || pronoun) {
    if (gender) genderText += `${gender}`;
    genderText += gender && pronoun ? ' (' : '';
    if (pronoun) genderText += `${pronoun}`;
    genderText += gender && pronoun ? ') ' : ' ';
    if (gender && recordString?.length > 0) genderText += recordString;
  }

  return genderText;
};

export const generateGenderText = (
  genderOffRecord: string,
  genderOnRecord: string,
  pronounsOffRecord: string[],
  pronounsOnRecord: string[],
): string => {
  let genderText = '';

  genderText += formGenderString(
    genderOnRecord,
    getPronounsValue(pronounsOnRecord),
    'on record',
  );
  if (genderText.length && (genderOffRecord || pronounsOffRecord?.length > 0))
    genderText += '\n';
  genderText += formGenderString(
    genderOffRecord,
    getPronounsValue(pronounsOffRecord),
    'off record',
  );

  return genderText;
};
export const generateTagFields = (values: string[]) => {
  const tagDefaults = {
    color: TagColor.white,
    isBorderRequired: true,
    isTag: true,
  };

  return values
    .filter((value) => value !== null && value !== '' && value !== 'Unknown')
    .map((value) => ({
      ...tagDefaults,
      value,
    }));
};

export const generateValueObject = (values: string[]) => ({
  fields: generateTagFields(values),
  isFlexWrapAllowed: true,
});
export const generateGenderTextForPatientHeader = (
  genderOffRecord: string,
  genderOnRecord: string,
  pronounsOffRecord: string[],
  pronounsOnRecord: string[],
): string => {
  let genderText = '';

  genderText += formGenderString(
    genderOnRecord,
    getPronounsValue(pronounsOnRecord),
    null,
  );
  genderText +=
    (genderOnRecord || pronounsOnRecord?.length > 0) &&
    (genderOffRecord || pronounsOffRecord?.length > 0)
      ? '| '
      : '';
  genderText += formGenderString(
    genderOffRecord,
    getPronounsValue(pronounsOffRecord),
    null,
  );
  return genderText;
};

export const formatAddress = (address: PatientAddress) => {
  if (!address) {
    return [];
  }

  const addressLine3 = [address.city, `${address.state} ${address.zip ?? ''}`]
    .filter(Boolean)
    .join(', ');

  const fields = [address?.addressLine1, address?.addressLine2, addressLine3];

  return fields.filter(Boolean).map((lines) => ({ value: lines }));
};

export const generateConsentTag = (hasGivenConsent?: boolean | null) => {
  if (hasGivenConsent == null) return null;

  const color = hasGivenConsent ? TagColor.green : TagColor.red;
  const value = hasGivenConsent
    ? ConsentToRecord.ConsentGiven
    : ConsentToRecord.ConsentNotGiven;

  return {
    fields: [
      {
        color,
        isTag: true,
        value,
      },
    ],
  };
};

interface SecondaryDetails {
  sex: string;
  genderOnRecord: string;
  pronounsOnRecord: string[];
  genderOffRecord: string;
  pronounsOffRecord: string[];
  ethnicities: string[];
  languages: string[];
  patientTags: string[];
  hasGivenRecordingConsent: boolean;
}
export const getSecondaryDetails = ({
  sex,
  genderOnRecord,
  pronounsOnRecord,
  genderOffRecord,
  pronounsOffRecord,
  ethnicities,
  languages,
  patientTags,
  hasGivenRecordingConsent,
}: SecondaryDetails) => [
  {
    id: 'sex-assigned-at-birth',
    isRequired: false,
    title: 'Sex assigned at birth',
    value: sex,
  },
  {
    id: 'gender',
    isRequired: false,
    title: 'Gender on record',
    value: genderOnRecord,
  },
  {
    id: 'pronouns-on-record',
    isRequired: false,
    title: 'Pronouns on record',
    value: pronounsOnRecord && generateValueObject(pronounsOnRecord),
  },
  {
    id: 'gender-off-record',
    isRequired: false,
    title: 'Gender off record',
    value: genderOffRecord,
  },
  {
    id: 'pronouns-off-record',
    isRequired: false,
    title: 'Pronouns off record',
    value: pronounsOffRecord && generateValueObject(pronounsOffRecord),
  },
  {
    id: 'race-ethnicity',
    isRequired: false,
    title: 'Race/Ethnicity',
    value: ethnicities && generateValueObject(ethnicities),
  },
  {
    id: 'languages-spoken',
    isRequired: false,
    title: 'Language(s) spoken',
    value: languages && generateValueObject(languages),
  },
  {
    id: 'tag',
    isRequired: false,
    title: 'Tags',
    value: patientTags && generateValueObject(patientTags),
  },
  {
    id: 'consent-to-record',
    isRequired: false,
    title: 'Consent to record',
    value: generateConsentTag(hasGivenRecordingConsent),
  },
];
export const getInitialFilterValue = (
  currentUser: User,
): PatientListFiltersType => {
  return {
    admitDateFrom: null,
    admitDateTo: null,
    careTeamMember: currentUser
      ? [
          {
            displayText: `${currentUser?.firstName ?? ''}${
              currentUser?.firstName && currentUser?.lastName ? ' ' : ''
            }${currentUser?.lastName ?? ''}`,
            value: currentUser.userId,
          },
        ]
      : [],
    episodeLevels: [],
    episodeStatus: [],
    episodeStatusReasons: [],
    insuranceVerificationStatus: [],
    isPatientPaused: false,
    medicalClearanceStatus: [],
    name: null,
    payor: [],
    payorProgram: [],
    primaryDiagnosis: [],
    psychClearanceStatuses: [],
    treatmentPrograms: [],
    universalStatuses: [],
  };
};

export const isValidPatientFilterType = (filter: PatientFilterType): boolean =>
  util.isValidString(filter?.displayText) && util.isValidString(filter?.value);

export const isValidPatientFilterTypeArray = (
  filters: PatientFilterType[],
): boolean => {
  if (Array.isArray(filters)) return filters.every(isValidPatientFilterType);
  return false;
};

export const validateFormatColumnFilterValue = (
  filterValue: string[],
  defaults: string[],
  initialValue: string[],
): string[] => [
  ...new Set(
    (util.isValidStringArray(filterValue) && filterValue?.length
      ? filterValue
      : initialValue
    )
      .filter(Boolean)
      .concat(defaults),
  ),
];

/**
 * When the filterValue is a validStringArray, we are creating a Set,
 * While creating the Set,
 * First, we are adding Default Columns
 * Second, validate the filterValue and add it (to remove invalid entries)
 * Third, We are adding allColumns (to add missing column names)
 */
export const validateFormatColumnSortValue = (
  filterValue: string[],
  allColumns: string[],
): string[] =>
  util.isValidStringArray(filterValue)
    ? [
        ...new Set([
          ...TABLE_DEFAULT_COLUMNS,
          ...filterValue.filter((value) => allColumns.includes(value)),
          ...allColumns,
        ]),
      ]
    : allColumns;

export const validateFormatPatientFilterValue = (
  patientFilters: PatientListFiltersType,
  currentUser: User,
): PatientListFiltersType => {
  const validatedPatientFilters: PatientListFiltersType =
    getInitialFilterValue(currentUser);

  if (isValidPatientFilterType(patientFilters?.admitDateFrom))
    validatedPatientFilters.admitDateFrom = patientFilters?.admitDateFrom;
  if (isValidPatientFilterType(patientFilters?.admitDateTo))
    validatedPatientFilters.admitDateTo = patientFilters?.admitDateTo;

  if (isValidPatientFilterTypeArray(patientFilters?.careTeamMember))
    validatedPatientFilters.careTeamMember = patientFilters?.careTeamMember;

  if (isValidPatientFilterTypeArray(patientFilters?.episodeStatus))
    validatedPatientFilters.episodeStatus = patientFilters?.episodeStatus;
  if (util.isValidStringArray(patientFilters?.insuranceVerificationStatus))
    validatedPatientFilters.insuranceVerificationStatus =
      patientFilters?.insuranceVerificationStatus;
  if (util.isValidStringArray(patientFilters?.medicalClearanceStatus))
    validatedPatientFilters.medicalClearanceStatus =
      patientFilters?.medicalClearanceStatus;
  if (util.isValidStringArray(patientFilters?.episodeLevels))
    validatedPatientFilters.episodeLevels = patientFilters?.episodeLevels;
  if (util.isValidStringArray(patientFilters?.treatmentPrograms))
    validatedPatientFilters.treatmentPrograms =
      patientFilters?.treatmentPrograms;
  if (util.isValidStringArray(patientFilters?.episodeStatusReasons))
    validatedPatientFilters.episodeStatusReasons =
      patientFilters?.episodeStatusReasons;
  if (util.isValidStringArray(patientFilters?.psychClearanceStatuses))
    validatedPatientFilters.psychClearanceStatuses =
      patientFilters?.psychClearanceStatuses;

  if (util.isValidString(patientFilters?.name))
    validatedPatientFilters.name = patientFilters?.name;

  if (util.isValidStringArray(patientFilters?.payor))
    validatedPatientFilters.payor = patientFilters?.payor;
  if (util.isValidStringArray(patientFilters?.payorProgram))
    validatedPatientFilters.payorProgram = patientFilters?.payorProgram;
  if (isValidPatientFilterTypeArray(patientFilters?.primaryDiagnosis))
    validatedPatientFilters.primaryDiagnosis = patientFilters?.primaryDiagnosis;
  validatedPatientFilters.isPatientPaused = patientFilters?.isPatientPaused;

  if (isValidPatientFilterTypeArray(patientFilters?.universalStatuses))
    validatedPatientFilters.universalStatuses =
      patientFilters?.universalStatuses;

  return validatedPatientFilters;
};

export const isFilterEmpty = (filter: PatientListFiltersType): boolean => {
  if (!filter) return true;

  const {
    admitDateFrom,
    admitDateTo,
    careTeamMember,
    episodeLevels,
    episodeStatus,
    episodeStatusReasons,
    insuranceVerificationStatus,
    isPatientPaused,
    medicalClearanceStatus,
    name,
    payor,
    payorProgram,
    primaryDiagnosis,
    psychClearanceStatuses,
    treatmentPrograms,
    universalStatuses,
  } = filter;
  return (
    !name &&
    !admitDateFrom?.value &&
    !admitDateTo?.value &&
    util.isEmptyArray(careTeamMember) &&
    util.isEmptyArray(episodeLevels) &&
    util.isEmptyArray(episodeStatus) &&
    util.isEmptyArray(episodeStatusReasons) &&
    util.isEmptyArray(insuranceVerificationStatus) &&
    util.isEmptyArray(medicalClearanceStatus) &&
    util.isEmptyArray(payor) &&
    util.isEmptyArray(payorProgram) &&
    util.isEmptyArray(primaryDiagnosis) &&
    util.isEmptyArray(psychClearanceStatuses) &&
    util.isEmptyArray(treatmentPrograms) &&
    !isPatientPaused &&
    util.isEmptyArray(universalStatuses)
  );
};

export const getPreferredFullName = (
  chosenName: string,
  firstName: string,
  lastName: string,
  useQuotes?: boolean,
): string => {
  const first = getPreferredFirstName(chosenName, firstName, useQuotes);
  const last = getName(lastName);
  return `${first} ${last}`.trim();
};

export const getPreferredFullNameV2 = (
  patient: Patient,
  verbose = false,
): string => {
  if (!patient) return null;
  const displayName = getPreferredFullName(
    patient.chosenName,
    patient.firstName,
    patient.lastName,
    false,
  );
  if (verbose && getName(patient.firstName) && getName(patient.chosenName)) {
    return `${displayName} (${getName(patient.firstName)})`;
  }
  return displayName;
};

export const getDiffInWeeks = (
  date1: string,
  date2?: string,
  fromFormat = 'LL/dd/yyyy',
): number => {
  if (!date1) {
    return -1;
  }

  const startDate = DateTime.fromFormat(date1, fromFormat);
  const endDate = date2
    ? DateTime.fromFormat(date2, fromFormat)
    : DateTime.local();
  return Math.ceil(endDate.diff(startDate, ['weeks']).weeks);
};

export const getFormattedDateFromString = (val: string): string =>
  val ? DateTime.fromSQL(val).toFormat('LL/dd/yyyy') : EMPTY_DATE_FORMAT;

export const getFormattedNoteActivityDateString = (date: Date): string =>
  date.toISOString();

export const getFormattedNoteActivityDateToDisplayString = (
  date: Date,
): string =>
  `${DateTime.fromJSDate(date).toFormat(
    'MMMM dd, yyyy',
  )} at ${DateTime.fromJSDate(date).toFormat('h:mm a')}`;

export const getParsedNoteActivityDate = (dateString: string): Date =>
  new Date(dateString);

export const getPatientAppSettingReadModeTextWithName = (
  name: string,
  hasAuthId: boolean,
  isAccountActive: boolean,
): string =>
  hasAuthId
    ? `${name} has an${isAccountActive ? '' : ' inactive'} Equip App account`
    : `${name} doesn't have an Equip App account.`;

export const getPatientCityAndState = (city: string, state: string): string =>
  [city || 'unknown', state].filter(Boolean).join(', ');

export const getPatientMatchStyle = (
  strength: string,
): {
  colorScheme?: string;
  bars?: number;
  text?: string;
} => {
  switch (strength) {
    case 'strong':
      return {
        bars: 3,
        colorScheme: 'teal',
        text: 'Strong',
      };

    case 'medium':
      return {
        bars: 2,
        colorScheme: 'yellow',
        text: 'Medium',
      };

    case 'weak':
      return {
        bars: 1,
        colorScheme: 'red',
        text: 'Weak',
      };

    default:
      return {};
  }
};

export const getPatientMatchCount = (
  matchingPatients: GetPatientMatches,
): number => {
  return (
    (matchingPatients?.fullMatches.length ?? 0) +
    (matchingPatients?.partialMatches.length ?? 0) +
    (matchingPatients?.weakMatches.length ?? 0)
  );
};

export const getPatientTimeZoneInfo = (timezoneValue: string): string => {
  if (!timezoneValue) return '';
  const timeZoneAbbr = DateTime.local().setZone(timezoneValue).toFormat('ZZZZ');
  if (timeZoneAbbr === 'Invalid DateTime') return timeZoneAbbr;
  const localTimeOfTimeZone = DateTime.local()
    .setZone(timezoneValue)
    .toFormat('t');

  return `(${timeZoneAbbr}, ${localTimeOfTimeZone} Local)`;
};

export const getPatientCityStateAndTimezone = (
  city: string,
  state: string,
  timezoneValue: string,
): string =>
  `${getPatientCityAndState(city, state)}${
    timezoneValue ? ` ${getPatientTimeZoneInfo(timezoneValue)}` : ''
  }`;

const surveyActivityToDisplayMapper: Record<
  SurveyActivityName,
  (date: Date) => string
> = {
  'survey complete': (date: Date) =>
    `Complete - ${DateTime.fromJSDate(date).toFormat(DateFormat.longDate)}`,
  'survey invitation': (date: Date) =>
    `Sent - ${DateTime.fromJSDate(date).toFormat(DateFormat.longDate)}`,
  'survey reminder': (date: Date) =>
    `Last reminder - ${DateTime.fromJSDate(date).toFormat(DateFormat.longDate)}`,
};

export const getPatientListSurveyActivities = (
  surveyActivities: PatientListSurveyActivity[],
  surveyName: string,
): Record<SurveyActivityName, string> => {
  const targetActivities = (surveyActivities ?? []).filter(
    ({ surveyName: sn }) => sn.toLowerCase() === surveyName.toLowerCase(),
  );
  const result = {} as Record<SurveyActivityName, string>;
  for (const { surveyActivityDate, surveyActivityName } of targetActivities) {
    result[surveyActivityName] = surveyActivityToDisplayMapper[
      surveyActivityName
    ](new Date(surveyActivityDate));
  }
  return result;
};

export const getPatientListNoteActivityDate = (note: PatientListNote): string =>
  note?.noteActivityDate &&
  DateTime.fromJSDate(new Date(note.noteActivityDate)).toFormat(
    DateFormat.longDate,
  );

// Text of HTML content, with blocks and line breaks separated by a space:
// '<p>a</p><p>b<br>c</p>' -> 'a b c'.
export const getHtmlText = (html: string): string =>
  new DOMParser()
    .parseFromString(
      html.replace(/<\/(p|li|h[1-6]|blockquote|pre)>|<br\s*\/?>/gi, '$& '),
      'text/html',
    )
    .body.textContent.replace(/\s+/g, ' ')
    .trim();

export const getPatientListNote = (note: string): string => {
  let result: string;

  try {
    result = (JSON.parse(note).blocks ?? [])
      .map(({ text = '' }) => text)
      .join(' ');
  } catch (_e) {
    // plain text is escaped into paragraphs first, so `<` in it stays text; HTML is unchanged
    result = note ? getHtmlText(coalescePlainTextToEditorV2Html(note)) : note;
  }
  return result;
};

export const hasSchedulingPermission = (
  userId: string,
  isProfileConnected: boolean,
  isSchedulingAdmin: boolean,
  careTeamMembers: CareTeamMember[],
): boolean =>
  !!isSchedulingAdmin ||
  !!(
    isProfileConnected &&
    careTeamMembers?.some((p) => p.providerExternalId === userId)
  );

export const toSentenceCaseFromCamelCase = (camelCase: string): string => {
  if (camelCase) {
    const result = camelCase.replace(/([A-Z])/g, ' $1');
    return result[0].toUpperCase() + result.substring(1).toLowerCase();
  }
  return '';
};

export const createCombinedNamesForFamilyTaskAssignees = (
  familyAssignees: FamilyTaskAssignee[],
): string => {
  let combinedNames = '';
  for (let i = 1; i < familyAssignees.length; i += 1) {
    combinedNames +=
      familyAssignees[i].name ||
      getPreferredFullName(
        familyAssignees[i].chosenName ?? '',
        familyAssignees[i].firstName,
        familyAssignees[i].lastName,
      );
    if (i !== familyAssignees.length - 1) {
      combinedNames += ', ';
    }
  }
  return combinedNames;
};

export const getFileType = (fileName: string): string => {
  if (!fileName) return null;
  const extension = getExtension(fileName);
  switch (extension) {
    case 'pdf':
      return 'PDF Document';

    case 'xls':
    case 'xlsx':
      return 'Excel Document';

    case 'doc':
    case 'docx':
      return 'Word Document';

    case 'bmp':
    case 'jpg':
    case 'jpeg':
    case 'gif':
    case 'png':
      return 'Image';

    default:
      return 'Document';
  }
};

export const getHighlightFields = (
  appendSurveyResult: SurveyResults | undefined,
): HighlightFields | undefined => {
  if (!appendSurveyResult || Object.keys(appendSurveyResult).length === 0) {
    return undefined;
  }
  const highlightFields: HighlightFields = {};
  Object.entries(appendSurveyResult).forEach(([key, value]) => {
    if (value !== undefined) {
      highlightFields[key] = true;
    }
  });
  return Object.keys(highlightFields).length > 0 ? highlightFields : undefined;
};

/**
 * Converts a given phone number to an +12194467334 format
 *
 * If the phone number is already in an international format (starts with '+1' or any '+' prefix),
 * it returns the number as is. Otherwise, it attempts to convert it using `convertDashedUSPhoneNumberToInternationalFormat`
 * function, which is presumably tailored for US phone numbers not already in international format.
 *
 * @param {string} [phone] - The phone number to be converted. Optional parameter.
 * @returns {string} - The converted phone number in international format, or the original number if it's
 * already in an appropriate format or if the input is undefined.
 */
export const convertPhoneNumberToInternationalFormat = (
  phone?: string,
): string => {
  if (!phone || phone.startsWith('+')) return phone;
  return convertDashedUSPhoneNumberToInternationalFormat(phone);
};

export const toPatientAddress = (
  addressField: GooglePlacesField,
): PatientAddress => {
  if (!addressField) {
    return null;
  }
  return {
    addressLine1: addressField.addressLine1,
    addressLine2: addressField.addressLine2,
    affiliationName: addressField.name,
    city: addressField.city,
    country: addressField.country,
    county: addressField.county,
    state: addressField.state,
    zip: addressField.zipCode,
  };
};

export const isShippingSameAsMainAddress = (
  shippingAddress: PatientAddress,
  mainAddress: PatientAddress,
) => {
  if (!shippingAddress || !mainAddress) {
    return false;
  }

  const {
    affiliationName,
    addressLine1,
    addressLine2,
    city,
    state,
    zip,
    county,
    country,
  } = mainAddress;

  const {
    affiliationName: shippingAffiliationName,
    addressLine1: shippingAddressLine1,
    addressLine2: shippingAddressLine2,
    city: shippingCity,
    state: shippingState,
    zip: shippingZip,
    county: shippingCounty,
    country: shippingCountry,
  } = shippingAddress;

  return (
    shippingAffiliationName === affiliationName &&
    shippingAddressLine1 === addressLine1 &&
    shippingAddressLine2 === addressLine2 &&
    shippingCity === city &&
    shippingState === state &&
    shippingZip === zip &&
    shippingCounty === county &&
    shippingCountry === country
  );
};
interface PhoneNumbers {
  phoneNumber: string | number;
  homePhone: string | number;
  officeNumber: string | number;
  faxNumber: string | number;
  secondaryPhoneNumber: string | number;
  officeExtension?: string | number;
}

export const getAddressDetails = (
  mainAddress: PatientAddress,
  shippingAddress: PatientAddress,
  timezone: string,
) => [
  {
    id: 'main-address',
    isRequired: true,
    title: 'Main address',
    value: {
      fields: formatAddress(mainAddress),
    },
  },
  {
    id: 'time-zone',
    isRequired: false,
    title: 'Timezone',
    value: timezone,
  },
  {
    id: 'shipping-address',
    isRequired: false,
    title: 'Shipping address',
    value: {
      fields: isShippingSameAsMainAddress(shippingAddress, mainAddress)
        ? [{ value: 'Same as main address' }]
        : formatAddress(shippingAddress),
    },
  },
];
const formattedNames = (name: string | null): string => {
  if (!name) {
    return null;
  }
  return name?.charAt(0)?.toUpperCase() + name?.slice(1);
};
export const formatCellPhoneNumber = (
  phoneNumber: string | number,
): string | null => {
  if (phoneNumber == null) {
    return null;
  }
  const phoneString = phoneNumber.toString();

  if (phoneString.startsWith('+1') && phoneString.length === 12) {
    return `(${phoneString.slice(2, 5)}) ${phoneString.slice(5, 8)}-${phoneString.slice(8, 12)}`;
  }

  if (phoneString.startsWith('+1') || !phoneString.startsWith('+1')) {
    return phoneString;
  }

  return null;
};
const getPhonesIconMap = ({
  phoneNumber,
  homePhone,
  officeNumber,
  faxNumber,
  secondaryPhoneNumber,
}: PhoneNumbers) => [
  {
    leftIcon: FAIconName.mobileRegular,
    value: formatCellPhoneNumber(phoneNumber),
  },
  {
    leftIcon: FAIconName.homeRegular,
    value: formatCellPhoneNumber(homePhone),
  },
  {
    leftIcon: FAIconName.briefCaseRegular,
    value: formatCellPhoneNumber(officeNumber),
  },
  {
    leftIcon: FAIconName.faxRegular,
    value: formatCellPhoneNumber(faxNumber),
  },
  {
    leftIcon: FAIconName.phoneRegular,
    value: formatCellPhoneNumber(secondaryPhoneNumber),
  },
];

const validPhonesIconMap = ({
  phoneNumber,
  homePhone,
  officeNumber,
  faxNumber,
  secondaryPhoneNumber,
}: PhoneNumbers) => {
  const names = getPhonesIconMap({
    faxNumber,
    homePhone,
    officeNumber,
    phoneNumber,
    secondaryPhoneNumber,
  });

  return names.filter((phone) => phone.value !== null && phone.value !== '');
};
interface PrimaryDetails {
  firstName: string;
  lastName: string;
  chosenName: string;
  formattedDob: string;
  mrn: string;
  email: string;
  contactPreferences: string[];
  phoneNumber: string | number;
  homePhone: string | number;
  officeNumber: string | number;
  faxNumber: string | number;
  secondaryPhoneNumber: string | number;
  isAccountActive: boolean;
}

interface PrimaryContactDetails {
  prefix: string;
  firstName: string;
  chosenName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  gender: string;
  pronouns: string[];
  formattedDob: string;
  ethnicities: string[];
}

interface SecondaryContactDetails {
  relationToPatient: string;
  contactPreferences: string[];
  email: string;
  isProxy: boolean;
  isSupport: boolean;
  phoneNumber: string | number;
  homePhone: string | number;
  officeNumber: string | number;
  faxNumber: string | number;
  secondaryPhoneNumber: string | number;
  languages: string[];
}

export const getPrimaryDetails = ({
  firstName,
  lastName,
  chosenName,
  formattedDob,
  mrn,
  email,
  contactPreferences,
  phoneNumber,
  homePhone,
  officeNumber,
  faxNumber,
  secondaryPhoneNumber,
  isAccountActive,
}: PrimaryDetails) => [
  {
    id: 'first-name',
    isRequired: true,
    title: 'First name',
    value: formattedNames(firstName),
  },
  {
    id: 'last-name',
    isRequired: true,
    title: 'Last name',
    value: formattedNames(lastName),
  },
  {
    id: 'chosen-name',
    isRequired: false,
    title: 'Chosen name',
    value: formattedNames(chosenName),
  },
  {
    id: 'dob',
    isRequired: true,
    title: 'Date of birth',
    value: {
      fields: [
        {
          leftIcon: FAIconName.calendarDayRegular,
          value: formattedDob,
        },
      ],
    },
  },
  {
    id: 'mrn',
    isRequired: false,
    title: 'MRN',
    value: mrn?.toString(),
  },
  {
    id: 'email',
    isRequired: true,
    title: 'Email',
    value: {
      fields: [{ leftIcon: FAIconName.envelopeRegular, value: email }],
    },
  },
  {
    id: 'phone',
    isRequired: isAccountActive ? true : false,
    title: 'Phone',
    value: {
      fields: validPhonesIconMap({
        faxNumber,
        homePhone,
        officeNumber,
        phoneNumber,
        secondaryPhoneNumber,
      }),
    },
  },
  {
    id: 'preffered-contact',
    isRequired: false,
    title: 'Preferred contact method',
    value: contactPreferences && generateValueObject(contactPreferences),
  },
];

export const getContactPrimaryDetails = ({
  chosenName,
  ethnicities,
  firstName,
  formattedDob,
  gender,
  lastName,
  middleName,
  prefix,
  pronouns,
  suffix,
}: PrimaryContactDetails) => [
  { id: 'prefix', title: 'Prefix', value: formattedNames(prefix) },
  {
    id: 'first-name',
    isRequired: true,
    title: 'First name',
    value: formattedNames(firstName),
  },
  {
    id: 'chosen-name',
    title: 'Chosen name',
    value: formattedNames(chosenName),
  },
  {
    id: 'middle-name',
    title: 'Middle name',
    value: formattedNames(middleName),
  },
  {
    id: 'last-name',
    isRequired: true,
    title: 'Last name',
    value: formattedNames(lastName),
  },
  {
    id: 'suffix',
    title: 'Suffix',
    value: formattedNames(suffix),
  },
  {
    id: 'gender',
    title: 'Gender',
    value: gender,
  },
  {
    id: 'pronouns',
    title: 'Pronouns',
    value: pronouns && generateValueObject(pronouns),
  },
  {
    id: 'dob',
    title: 'Date of birth',
    value: {
      fields: [
        {
          leftIcon: FAIconName.calendarDayRegular,
          value: formattedDob,
        },
      ],
    },
  },

  {
    id: 'race-ethnicity',
    isRequired: false,
    title: 'Race/Ethnicity',
    value: ethnicities && generateValueObject(ethnicities),
  },
];
export const getContactSecondaryDetails = ({
  contactPreferences,
  email,
  faxNumber,
  homePhone,
  isProxy,
  isSupport,
  languages,
  officeNumber,
  phoneNumber,
  relationToPatient,
  secondaryPhoneNumber,
}: SecondaryContactDetails) => [
  {
    id: 'relation-to-patient',
    isRequired: true,
    title: 'Relation to patient',
    value: relationToPatient,
  },
  {
    id: 'contact-preference',
    title: 'Contact preference',
    value: contactPreferences && generateValueObject(contactPreferences),
  },
  {
    id: 'email',
    isRequired: isSupport || isProxy,
    title: 'Email',
    value: {
      fields: [{ leftIcon: FAIconName.envelopeRegular, value: email }],
    },
  },
  {
    id: 'phone',
    isRequired: isSupport || isProxy,
    title: 'Phone',
    value: {
      fields: validPhonesIconMap({
        faxNumber,
        homePhone,
        officeNumber,
        phoneNumber,
        secondaryPhoneNumber,
      }),
    },
  },
  {
    id: 'languages-spoken',
    title: 'Language(s) spoken',
    value: languages && generateValueObject(languages),
  },
];

export const getAllPatientMatches = (
  patientMatchesResponse: GetPatientMatches,
): PatientMatch[] => {
  return [
    ...(patientMatchesResponse?.fullMatches ?? []),
    ...(patientMatchesResponse?.partialMatches ?? []),
    ...(patientMatchesResponse?.weakMatches ?? []),
  ];
};

export const levelTagColorMapper: Record<string, TagColor> = {
  blue: TagColor.periwinkle,
  green: TagColor.green,
  orange: TagColor.orange,
  purple: TagColor.purple,
  red: TagColor.red,
  teal: TagColor.periwinkle,
  yellow: TagColor.marigold,
};

export const treatmentEpisodeColorMapper: Record<
  TreatmentEpisodeStatus,
  TagColor
> = {
  [TreatmentEpisodeStatus.DISCHARGED]: TagColor.gray,
  [TreatmentEpisodeStatus.IN_TREATMENT]: TagColor.green,
  [TreatmentEpisodeStatus.NEVER_STARTED]: TagColor.gray,
  [TreatmentEpisodeStatus.PAUSED]: TagColor.gray,
  [TreatmentEpisodeStatus.UNKNOWN]: TagColor.gray,
  [TreatmentEpisodeStatus.VOID]: TagColor.red,
};

const newStatusStyles: PatientStatusStyles = {
  backgroundColor: 'tertiary.marigold.background',
  color: 'tertiary.marigold.text',
  tagColor: TagColor.marigold,
};
const activeStatusStyles: PatientStatusStyles = {
  backgroundColor: 'primary.10',
  color: 'primary.100',
  tagColor: TagColor.green,
};
const inactiveStatusStyles: PatientStatusStyles = {
  backgroundColor: 'neutral.background.secondary',
  color: 'neutral.secondary',
  tagColor: TagColor.gray,
};

export const episodeStatusMapper: Record<EpisodeStatus, PatientStatusStyles> = {
  [EpisodeStatus.NEW]: newStatusStyles,
  [EpisodeStatus.ASSESSING]: newStatusStyles,
  [EpisodeStatus.ENROLLED]: activeStatusStyles,
  [EpisodeStatus.IN_TREATMENT]: activeStatusStyles,
  [EpisodeStatus.PAUSED]: newStatusStyles,
  [EpisodeStatus.CLOSED]: inactiveStatusStyles,
};

export const universalStatusMapper: Record<
  PatientUniversalStatusCode,
  PatientStatusStyles
> = {
  [PatientUniversalStatusCode.NEW]: newStatusStyles,
  [PatientUniversalStatusCode.FIELDED]: newStatusStyles,
  [PatientUniversalStatusCode.QUALIFIED]: newStatusStyles,
  [PatientUniversalStatusCode.ASSESSING]: newStatusStyles,
  [PatientUniversalStatusCode.ONBOARDING]: activeStatusStyles,
  [PatientUniversalStatusCode.CLOSED]: inactiveStatusStyles,
  [PatientUniversalStatusCode.IN_TREATMENT]: activeStatusStyles,
  [PatientUniversalStatusCode.DISCHARGED]: inactiveStatusStyles,
  [PatientUniversalStatusCode.PAUSED]: newStatusStyles,
  [PatientUniversalStatusCode.NEVER_STARTED]: inactiveStatusStyles,
  [PatientUniversalStatusCode.UNKNOWN]: inactiveStatusStyles,
};

export const sortPatientsAndSupports = (
  participants: PatientGroupClassListItem[],
): PatientGroupClassListItem[] => {
  const patient =
    participants?.filter((groupClass) => groupClass.userType == 'patient') ??
    [];

  const supports =
    participants
      ?.map((participant) => {
        if (participant.userType == 'support') {
          return {
            ...participant,
            name: getPreferredFirstName(
              participant.chosenName,
              participant.firstName,
              false,
            ),
          };
        }
        return null;
      })
      ?.filter((supportItem) => supportItem !== null)
      .sort((supportA, supportB) =>
        supportA.name.localeCompare(supportB.name),
      ) ?? [];

  return [...patient, ...supports];
};

export const determinePatientInquiryStatus = (
  patientInquirySummaries: GetPatientInquiriesSummaryResponse[],
  patientStatus: string,
) => {
  if (!patientInquirySummaries || !Array.isArray(patientInquirySummaries)) {
    return null;
  }

  let openInquiries = patientInquirySummaries?.filter(
    (summary: GetPatientInquiriesSummaryResponse) =>
      summary.status.name !== InquiryStatusValue.CLOSED &&
      summary.status.name !== InquiryStatusValue.UNKNOWN,
  );
  openInquiries = openInquiries.filter(
    (inquiry) => inquiry.status.name === patientStatus,
  );

  const closedInquiries = patientInquirySummaries?.filter(
    (summary: GetPatientInquiriesSummaryResponse) =>
      summary.status.name === InquiryStatusValue.CLOSED ||
      summary.status.name === InquiryStatusValue.UNKNOWN,
  );
  if (openInquiries && openInquiries.length === 1) {
    return openInquiries[0];
  } else if (openInquiries && openInquiries.length > 1) {
    const statusOrder: InquiryStatusValue[] = [
      InquiryStatusValue.ONBOARDING,
      InquiryStatusValue.ASSESSING,
      InquiryStatusValue.QUALIFIED,
      InquiryStatusValue.FIELDED,
      InquiryStatusValue.NEW,
    ];
    openInquiries.sort((a, b) => {
      const statusComparison =
        statusOrder.indexOf(a.status.name as InquiryStatusValue) -
        statusOrder.indexOf(b.status.name as InquiryStatusValue);
      if (statusComparison === 0) {
        return (
          new Date(a.inquiryDate).getTime() - new Date(b.inquiryDate).getTime()
        );
      }
      return statusComparison;
    });
    return openInquiries[0];
  }

  if (closedInquiries && closedInquiries.length > 0) {
    closedInquiries.sort(
      (a, b) =>
        new Date(b.inquiryDate).getTime() - new Date(a.inquiryDate).getTime(),
    );
    return closedInquiries[0];
  }
  return null;
};

export const getIsShippingAddressFieldEmpty = (
  shippingAddress: GooglePlacesField,
) => {
  return (
    isEmpty(shippingAddress?.addressLine1) &&
    isEmpty(shippingAddress?.addressLine2) &&
    isEmpty(shippingAddress?.city) &&
    isEmpty(shippingAddress?.state) &&
    isEmpty(shippingAddress?.zipCode)
  );
};
export const getIsShippingAddressAllFieldEmptyForBodyTrace = (
  shippingAddress: CustomShippingAddress,
) => {
  return (
    isEmpty(shippingAddress?.addressLine1) &&
    isEmpty(shippingAddress?.zipCode) &&
    isEmpty(shippingAddress?.state) &&
    isEmpty(shippingAddress?.city)
  );
};
export const getIsShippingAddressFieldEmptyForBodyTrace = (
  shippingAddress: CustomShippingAddress,
) => {
  return (
    isEmpty(shippingAddress?.addressLine1) ||
    isEmpty(shippingAddress?.zipCode) ||
    isEmpty(shippingAddress?.state) ||
    isEmpty(shippingAddress?.city)
  );
};

export const patientStatesEnumMenuOptionsMapper = (
  options: { label: string; externalId: string }[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label, externalId }) => ({
    label,
    value: externalId,
  }));
};

export const getFormattedOptions = (
  options: string[],
): UniversalSelectOption[] => {
  return (options ?? []).map((value) => ({
    label: value,
    value,
  }));
};

export const handleDisableOption = (
  option: UniversalSelectOption,
  selectedOptions: UniversalSelectOption[],
) => {
  const selectedValues = new Set(selectedOptions.map((option) => option.value));
  if (selectedValues.has(PREFER_NOT_TO_ANSWER_LABEL)) {
    return option.value !== PREFER_NOT_TO_ANSWER_LABEL;
  }

  return option.value === PREFER_NOT_TO_ANSWER_LABEL && selectedValues.size > 0;
};

export const patientEnumMenuOptionsMapper = (
  options: MultiSelectOptions[],
): UniversalSelectOption[] => {
  return (options ?? []).map(({ label }: MultiSelectOptions) => ({
    label,
    value: label,
  }));
};

export const convertToUSDateFormat = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-');
  return `${month}/${day}/${year}`;
};

export const convertToISOFormat = (dateStr: string): string => {
  const [month, day, year] = dateStr.split('/');
  return `${year}-${month}-${day}`;
};

export const isValidDateOfBirth = (dob: string): boolean => {
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12][0-9]|3[01])\/(19|20)\d\d$/;
  if (!dateRegex.test(dob)) {
    return false;
  }

  const [month, day, year] = dob.split('/').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return false;
  }
  return !util.isDateOfBirthInFuture(date);
};

export const getPatientEditPayloadData = (
  editForm: EditBasicInfoFormState,
  isShippingAddressFieldEmpty: boolean,
) => {
  const shippingAddressData = !isShippingAddressFieldEmpty
    ? {
        addressLine1: editForm?.shippingAddress.addressLine1,
        addressLine2: editForm?.shippingAddress.addressLine2,
        affiliationName: null,
        city: editForm?.shippingAddress.city,
        country: '',
        county: '',
        latitude: editForm?.shippingAddress.latitude,
        longitude: editForm?.shippingAddress.longitude,
        state: editForm?.shippingAddress.state,
        zip: editForm?.shippingAddress.zipCode,
      }
    : null;

  const body: Patient = {
    addressLine1: editForm.mainAddress.addressLine1,
    addressLine2: editForm.mainAddress?.addressLine2,
    affiliationName: null,
    chosenName: editForm.personalInfo.chosenName?.trim(),
    city: editForm.mainAddress.city,
    contactPreferences: editForm.personalInfo.contactPreferences,
    country: '',
    county: '',
    dateOfBirth: editForm.personalInfo.dateOfBirth
      ? convertToISOFormat(editForm.personalInfo.dateOfBirth)
      : '',
    email: editForm.personalInfo.email?.trim(),
    hasGivenRecordingConsent: editForm.demographics.hasGivenRecordingConsent,
    ethnicities: editForm.demographics.ethinicities,
    faxNumber: convertPhoneNumberToInternationalFormat(
      editForm.phonesField.fax,
    ),
    firstHeardAboutEquipExtId: editForm.demographics.firstHeardAboutEquipExtId,
    firstName: editForm.personalInfo.firstName.trim(),
    genderOffRecord: editForm.demographics.genderOffRecord,
    genderOnRecord: editForm.demographics.genderOnRecord,
    homePhone: convertPhoneNumberToInternationalFormat(
      editForm.phonesField.home,
    ),
    languages: editForm.demographics.languagesSpoken,
    lastName: editForm.personalInfo.lastName.trim(),
    latitude: editForm.mainAddress.latitude,
    longitude: editForm.mainAddress.longitude,
    mrn: editForm.personalInfo.mrn?.trim(),
    officeExtension: editForm.phonesField.extension,
    officeNumber: convertPhoneNumberToInternationalFormat(
      editForm.phonesField.office,
    ),
    phoneNumber: convertPhoneNumberToInternationalFormat(
      editForm.phonesField.mobile,
    ),
    pronounsOffRecord: editForm.demographics.pronounsOffRecord,
    pronounsOnRecord: editForm.demographics.pronounsOnRecord,
    secondaryCellPhone: convertPhoneNumberToInternationalFormat(
      editForm.phonesField.other,
    ),
    sex: editForm.demographics.sex,
    shippingAddress: shippingAddressData,
    state: editForm.mainAddress.state,
    timezone: editForm.mainAddress.timezone,
    userId: editForm.userId,
    zip: editForm.mainAddress.zipCode,
  };
  return body;
};

export const validatePhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) return false;

  const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');

  const isValidFormat = /^\+?\d+$/.test(cleanedNumber);
  if (!isValidFormat) return false;

  const isInternational = cleanedNumber.startsWith('+');
  if (isInternational) {
    return cleanedNumber.length <= 15;
  } else {
    return isPossiblePhoneNumber(cleanedNumber, 'US');
  }
};

export const getRequiredFields = (): string[] => {
  return ['addressLine1', 'city', 'state', 'zipCode'];
};

export const isUniversalStatusOpen = (universalStatusCode: string): boolean =>
  openUniversalStatuses.includes(
    universalStatusCode as PatientUniversalStatusCode,
  );

export const shouldShowOrderLabButtonBasedOnProviderType = (
  providerType: string | null | undefined,
): boolean => {
  if (!providerType) {
    return false;
  }
  return Object.values(LabOrderEligibleProviderType).includes(
    providerType as LabOrderEligibleProviderType,
  );
};

export const shouldShowOrderLabButton = (
  currentUserProviderType: string,
  patientStatus: string | null,
): boolean =>
  shouldShowOrderLabButtonBasedOnProviderType(currentUserProviderType) &&
  !!patientStatus &&
  !disallowedUniversalStatusesForOrderingLab.includes(
    patientStatus as PatientUniversalStatusCode,
  );

export const isShippingSameAsMainAddressV2 = (
  shippingAddress: PatientAddress,
  mainAddress: PatientAddress,
) => {
  if (!shippingAddress || !mainAddress) {
    return false;
  }

  const { addressLine1, addressLine2, city, state, zip } = mainAddress;

  const {
    addressLine1: shippingAddressLine1,
    addressLine2: shippingAddressLine2,
    city: shippingCity,
    state: shippingState,
    zip: shippingZip,
  } = shippingAddress;

  return (
    shippingAddressLine1 === addressLine1 &&
    shippingAddressLine2 === addressLine2 &&
    shippingCity === city &&
    shippingState === state &&
    shippingZip === zip
  );
};

export const getTaskAssigneeName = ({
  chosenName,
  firstName,
  lastName,
  accountActive,
}: FamilyTaskAssignee) => {
  let fullName = getPreferredFullName(chosenName, firstName, lastName);

  if (!accountActive) {
    fullName = `${fullName} (Inactive)`;
  }

  return fullName;
};

export const getFileKey = (file: File) => {
  const name = file.name ?? 'unknown';
  const size = file.size ?? 0;
  return `${name}-${size}`;
};

const shouldHideAttribute = (
  input: string | string[],
  enumTarget: typeof CommonLanguage | typeof CommonTreatmentProgram,
): boolean => {
  const valuesToHide = new Set(
    Object.values(enumTarget).map((v) => v.toLowerCase()),
  );
  if (Array.isArray(input)) {
    if (input.length === 0) return false;
    return input.every((v: string) => valuesToHide.has(v.trim().toLowerCase()));
  }
  if (input == undefined || input.trim().length === 0) return false;
  return valuesToHide.has(input.trim().toLowerCase());
};

export const shouldHideLanguage = (input: string | string[]): boolean =>
  shouldHideAttribute(input, CommonLanguage);

export const shouldHideTreatmentProgram = (input: string | string[]): boolean =>
  shouldHideAttribute(input, CommonTreatmentProgram);
